<template>
  <main class="flyer-page print-friendly">
    <div class="flyer-container">
      <!-- Header -->
      <header class="flyer-header">
        <div class="logo-section">
          <img src="/assets/img/logo.png" alt="Space Coast STEM Logo" class="logo" />
        </div>
        <h1>Every Student Deserves Access to Technology</h1>
        <p class="tagline">A 5-Step Plan to Bridge the Digital Divide in Brevard County</p>
      </header>

      <!-- The Problem -->
      <section class="problem-section">
        <h2>The Problem</h2>
        <div class="stat-highlight">
          <p>Many students in Brevard County schools don't have access to a computer at home</p>
        </div>
      </section>

      <!-- The Discovery -->
      <section class="discovery-section">
        <h2>The Opportunity</h2>
        <p>School districts across the country sell decommissioned classroom laptops in bulk on government surplus sites like <strong>GovDeals.com</strong></p>
        <div class="cost-highlight">
          <span class="price">~$5</span>
          <span class="per-unit">per laptop</span>
          <p class="when-bulk">when purchased in bulk (500+ units)</p>
        </div>
        <p class="solution-text">At this price point, ensuring every student in Brevard County has access to technology becomes achievable.</p>
      </section>

      <!-- The Plan -->
      <section class="plan-section">
        <h2>The 5-Step Plan</h2>
        <div class="steps-compact">
          <div class="step-item completed">
            <span class="step-number">✓</span>
            <div class="step-info">
              <strong>Build a Platform</strong>
              <span>Website and teacher portal to manage inventory</span>
            </div>
          </div>
          <div class="step-item">
            <span class="step-number">2</span>
            <div class="step-info">
              <strong>Raise Funds</strong>
              <span>Secure funding to purchase wholesale lot</span>
            </div>
          </div>
          <div class="step-item">
            <span class="step-number">3</span>
            <div class="step-info">
              <strong>Purchase Wholesale Lot</strong>
              <span>Buy decommissioned laptops at wholesale prices</span>
            </div>
          </div>
          <div class="step-item">
            <span class="step-number">4</span>
            <div class="step-info">
              <strong>Host Refurbishment Event</strong>
              <span>Weekend-long volunteer event to prepare devices</span>
            </div>
          </div>
          <div class="step-item">
            <span class="step-number">5</span>
            <div class="step-info">
              <strong>Distribute to Students</strong>
              <span>Get laptops to students through their schools</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Ongoing Work -->
      <section class="ongoing-section">
        <h2>What We're Doing Now</h2>
        <div class="ongoing-grid">
          <div class="ongoing-item">
            <strong>Accepting Donations</strong>
            <p>We refurbish donated laptops from individuals and businesses</p>
          </div>
          <div class="ongoing-item">
            <strong>Working with Teachers</strong>
            <p>Partnering with Brevard County schools to identify students in need</p>
          </div>
          <div class="ongoing-item">
            <strong>Engaging Officials</strong>
            <p>Creating a direct pipeline for decommissioned school laptops</p>
          </div>
        </div>
      </section>

      <!-- CTA Footer -->
      <footer class="flyer-footer">
        <h2>How You Can Help</h2>
        <div class="cta-grid">
          <div class="cta-item">
            <strong>Donate</strong>
            <p>Money or equipment</p>
          </div>
          <div class="cta-item">
            <strong>Volunteer</strong>
            <p>Join our refurbishment events</p>
          </div>
          <div class="cta-item">
            <strong>Spread the Word</strong>
            <p>Share our mission</p>
          </div>
        </div>
        <div class="contact-info">
          <p><strong>Visit:</strong> spacecoaststem.org</p>
          <p><strong>Email:</strong> contact@spacecoaststem.org</p>
        </div>
      </footer>
    </div>
  </main>
</template>

<script setup lang="ts">
// Print-friendly static page
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');

.flyer-page {
  font-family: 'Inter', sans-serif;
  background: white;
  min-height: 100vh;
}

.flyer-container {
  max-width: 8.5in;
  margin: 0 auto;
  padding: 0.5in;
  background: white;
}

/* Header */
.flyer-header {
  text-align: center;
  margin-bottom: 1.5rem;
  border-bottom: 3px solid #2c4a6b;
  padding-bottom: 1rem;
}

.logo-section {
  margin-bottom: 0.75rem;
}

.logo {
  height: 60px;
  width: auto;
}

.flyer-header h1 {
  font-size: 1.75rem;
  font-weight: 800;
  color: #2c4a6b;
  margin: 0 0 0.5rem 0;
  line-height: 1.2;
}

.tagline {
  font-size: 1rem;
  color: #666;
  margin: 0;
}

/* Problem Section */
.problem-section {
  margin-bottom: 1.5rem;
}

.problem-section h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #e94f3a;
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-highlight {
  background: #f5f7fa;
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  border-left: 4px solid #e94f3a;
}

.big-number {
  font-size: 2.5rem;
  font-weight: 800;
  color: #e94f3a;
  display: block;
  line-height: 1;
  margin-bottom: 0.5rem;
}

.stat-highlight p {
  font-size: 0.95rem;
  color: #333;
  margin: 0;
  line-height: 1.4;
}

/* Discovery Section */
.discovery-section {
  margin-bottom: 1.5rem;
}

.discovery-section h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c4a6b;
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.discovery-section > p {
  font-size: 0.9rem;
  line-height: 1.5;
  color: #333;
  margin-bottom: 1rem;
}

.cost-highlight {
  background: linear-gradient(135deg, #2c4a6b 0%, #3ba3d1 100%);
  color: white;
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  margin-bottom: 0.75rem;
}

.price {
  font-size: 2.5rem;
  font-weight: 800;
  display: inline-block;
  margin-right: 0.5rem;
}

.per-unit {
  font-size: 1rem;
  font-weight: 600;
}

.when-bulk {
  font-size: 0.85rem;
  margin: 0.25rem 0 0 0;
  opacity: 0.9;
}

.solution-text {
  font-size: 0.9rem;
  font-weight: 600;
  color: #2c4a6b;
  text-align: center;
  margin: 0;
}

/* Plan Section */
.plan-section {
  margin-bottom: 1.5rem;
}

.plan-section h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c4a6b;
  margin: 0 0 0.75rem 0;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.steps-compact {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f5f7fa;
  border-radius: 6px;
  border-left: 3px solid #2c4a6b;
}

.step-item.completed {
  background: #e8f5e9;
  border-left-color: #4caf50;
}

.step-number {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  background: #2c4a6b;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
}

.step-item.completed .step-number {
  background: #4caf50;
  font-size: 1rem;
}

.step-info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  flex: 1;
}

.step-info strong {
  font-size: 0.95rem;
  color: #2c4a6b;
}

.step-info span {
  font-size: 0.8rem;
  color: #666;
}

/* Ongoing Section */
.ongoing-section {
  margin-bottom: 1.5rem;
}

.ongoing-section h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c4a6b;
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.ongoing-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
}

.ongoing-item {
  background: #f5f7fa;
  padding: 0.75rem;
  border-radius: 6px;
  text-align: center;
}

.ongoing-item strong {
  display: block;
  font-size: 0.9rem;
  color: #2c4a6b;
  margin-bottom: 0.25rem;
}

.ongoing-item p {
  font-size: 0.8rem;
  color: #666;
  margin: 0;
  line-height: 1.3;
}

/* Footer */
.flyer-footer {
  border-top: 3px solid #2c4a6b;
  padding-top: 1rem;
  text-align: center;
}

.flyer-footer h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #e94f3a;
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.cta-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.cta-item {
  background: linear-gradient(135deg, #e94f3a 0%, #d43d2a 100%);
  color: white;
  padding: 0.75rem;
  border-radius: 6px;
}

.cta-item strong {
  display: block;
  font-size: 1rem;
  margin-bottom: 0.25rem;
}

.cta-item p {
  font-size: 0.8rem;
  margin: 0;
  opacity: 0.95;
}

.contact-info {
  margin-top: 1rem;
}

.contact-info p {
  font-size: 0.9rem;
  color: #333;
  margin: 0.25rem 0;
}

/* Print Styles */
@media print {
  .flyer-page {
    background: white;
  }

  .flyer-container {
    padding: 0.25in;
    max-width: none;
  }

  @page {
    margin: 0.25in;
    size: letter portrait;
  }

  /* Preserve colors and backgrounds */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  /* Prevent page breaks inside elements */
  .stat-highlight,
  .cost-highlight,
  .step-item,
  .ongoing-item,
  .cta-item {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /* Prevent page breaks after headers */
  h2 {
    page-break-after: avoid;
    break-after: avoid;
  }

  /* Preserve grid layouts */
  .steps-compact,
  .ongoing-grid,
  .cta-grid {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /* Ensure contact info stays together */
  .contact-info {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /* Preserve flexbox and grid layouts */
  .steps-compact {
    display: flex !important;
  }

  .ongoing-grid,
  .cta-grid {
    display: grid !important;
  }
}

/* Responsive for screen viewing */
@media (max-width: 768px) {
  .flyer-container {
    padding: 1rem;
  }

  .ongoing-grid,
  .cta-grid {
    grid-template-columns: 1fr;
  }

  .flyer-header h1 {
    font-size: 1.5rem;
  }

  .big-number,
  .price {
    font-size: 2rem;
  }
}
</style>
